import { Badge } from "@/components/ui/badge";
import { Flame, Clock } from "lucide-react";
import { getSaleInfo, formatSaleEnds } from "@/lib/saleHelpers";

interface PriceDisplayProps {
  price?: number | null;
  previousPrice?: number | null;
  availability?: string | null;
  lastChecked?: string | null;
  priceDropAt?: string | null;
  saleEndDate?: string | null;
  className?: string;
}

function relativeTime(iso?: string | null): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return null;
}

export function PriceDisplay({
  price,
  previousPrice,
  availability,
  lastChecked,
  priceDropAt,
  saleEndDate,
  className = "",
}: PriceDisplayProps) {
  if (!price) return null;
  const sale = getSaleInfo({ price, previousPrice, priceDropAt, saleEndDate });
  const oos = availability && availability !== "in_stock";
  const checked = relativeTime(lastChecked);

  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
      <span className="font-bold text-lg text-foreground">£{price.toLocaleString()}</span>
      {sale.isOnSale && (
        <>
          <span className="text-sm text-muted-foreground line-through">£{previousPrice!.toLocaleString()}</span>
          {sale.percentOff !== null && (
            <Badge className="bg-[#dc2626] text-white hover:bg-[#dc2626] font-bold">-{sale.percentOff}%</Badge>
          )}
          {sale.justDropped && (
            <Badge className="bg-[#16a34a] text-white hover:bg-[#16a34a] gap-1">
              <Flame className="h-3 w-3" aria-hidden /> Just dropped
            </Badge>
          )}
          {sale.saleEndsSoon && sale.saleEndsAt && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-900 hover:bg-amber-100 gap-1">
              <Clock className="h-3 w-3" aria-hidden /> Sale {formatSaleEnds(sale.saleEndsAt)}
            </Badge>
          )}
        </>
      )}
      {oos && <Badge variant="secondary" className="bg-amber-100 text-amber-900 hover:bg-amber-100">Limited stock</Badge>}
      {checked && <span className="text-xs text-muted-foreground">Checked {checked}</span>}
    </span>
  );
}
